// Superstate LLC — full EN/ES translation dictionary
export const translations = {
  en: {
    nav: {
      home: "Home",
      services: "Services",
      howItWorks: "How It Works",
      whyUs: "Why Us",
      blog: "Blog",
      faq: "FAQ",
      testimonials: "Testimonials",
      quote: "Get a Quote",
      callBtn: "Call 786-543-8906",
    },
    hero: {
      badge: "Nationwide Dispatch · 49 States",
      headlineWords: ["KEEP", "YOUR", "WHEELS", "MOVING.", "KEEP", "YOUR", "REVENUE", "GROWING."],
      goldWords: ["MOVING.", "GROWING."],
      sub: "Superstate LLC handles your dispatching, broker negotiations, and load planning — so you can stay focused on the road and maximize every mile.",
      ctaPrimary: "Get a Free Quote",
      ctaSecondary: "Call Now: 786-543-8906",
      scroll: "Scroll",
      trustBadges: [
        "Fast 24-Hour Response",
        "Dedicated Dispatcher",
        "Personalized Service",
        "Built for Owner-Ops & Fleets",
      ],
      ariaHeadline: "Keep your wheels moving. Keep your revenue growing.",
    },
    stats: [
      { value: 500, suffix: "+", label: "Loads Dispatched Monthly" },
      { value: 98, suffix: "%", label: "On-Time Load Coverage" },
      { value: 24, suffix: "/7", label: "Live Dispatcher Support" },
      { value: 0, prefix: "$", label: "Hidden Fees. Ever." },
    ],
    about: {
      eyebrow: "About Superstate LLC",
      headingStart: "YOUR BACK OFFICE.",
      headingEnd: "HANDLED.",
      p1: "We're not just another dispatch service — we're a business growth partner for truckers. Our team fights for the best rates, handles broker communication, manages your paperwork, and keeps your trucks loaded so you never sit idle.",
      p2: "Whether you're a solo owner-operator or running a 20-truck fleet, we work as your personal business managers — so you can focus on driving and scaling.",
      capabilitiesTitle: "Core Capabilities",
      capabilities: [
        "Truckload Dispatching (FTL & LTL)",
        "Load Negotiation & Rate Maximization",
        "Broker & Shipper Communication",
        "Route Planning & Lane Optimization",
        "Rate Confirmation Handling",
        "Paperwork & Admin Support",
        "Compliance Guidance",
        "Dedicated Account Management",
      ],
    },
    services: {
      eyebrow: "What We Do",
      headingLine1: "DISPATCH SOLUTIONS",
      headingLine2: "BUILT FOR EVERY RIG.",
      sub: "Ten pillars of service, one relentless mission: keep your trucks loaded and your revenue climbing.",
      items: [
        { title: "Truckload Dispatching", desc: "Full truckload freight matched to your lanes and schedule — consistent, profitable miles." },
        { title: "Reefer Dispatch", desc: "Temperature-controlled loads handled with precision and attention to every detail." },
        { title: "Dry Van Dispatch", desc: "High-volume dry freight across top-paying lanes with fast confirmation turnaround." },
        { title: "Flatbed Dispatch", desc: "Oversized and specialized loads negotiated for maximum rate and minimum headache." },
        { title: "Box Truck Dispatch", desc: "Local and regional loads tailored for smaller fleets and independent box-truck operators." },
        { title: "Broker Communication", desc: "We deal with brokers so you never have to — calls, emails, rate cons, all of it." },
        { title: "Route & Lane Planning", desc: "Smart routing that cuts deadhead and boosts profit per mile on every haul." },
        { title: "Rate Confirmation & Paperwork", desc: "Every document handled, every time — rate cons, BOLs, invoicing, and compliance." },
        { title: "24/7 Dispatcher Support", desc: "A real person on the line when you need us — day, night, weekend, holiday." },
        { title: "Carrier Onboarding", desc: "New authority? We help you get set up fast and rolling within 24–48 hours." },
      ],
    },
    why: {
      eyebrow: "Why Superstate",
      headingLine1: "WE DON'T JUST DISPATCH.",
      headingLine2: "WE DRIVE YOUR BUSINESS FORWARD.",
      quote: [
        "\"Most dispatchers just find loads. We find the ",
        "RIGHT loads",
        " — at the ",
        "RIGHT rates",
        " — on the ",
        "RIGHT lanes",
        " for YOUR business.\"",
      ],
      differentiators: [
        { title: "Dedicated Dispatcher", desc: "A single point of contact who knows your business, your preferences, and your lanes." },
        { title: "Profit-First Load Selection", desc: "We only book loads that make sense for your bottom line — not just to fill the board." },
        { title: "Zero Downtime Focus", desc: "We work ahead of your schedule so your next load is locked before you finish the last one." },
        { title: "Transparent Pricing", desc: "Flat dispatch fee. No surprises. No hidden cuts. No fine print." },
        { title: "Real Communication", desc: "We answer calls, return texts, and stay on top of your freight — always." },
        { title: "Scalable for Fleets", desc: "Whether you have 1 truck or 20, our systems and team scale with you." },
      ],
    },
    how: {
      eyebrow: "How It Works",
      headingStart: "SIMPLE PROCESS.",
      headingEnd: "SERIOUS RESULTS.",
      steps: [
        { title: "Submit Your Info", desc: "Fill out our quick quote form with your equipment type, lanes, and dispatch needs." },
        { title: "We Learn Your Business", desc: "Your dedicated dispatcher gets to know your preferences, rates, and goals." },
        { title: "We Find & Negotiate", desc: "We search the market, negotiate with brokers, and secure profitable freight." },
        { title: "You Drive. You Get Paid.", desc: "Hit the road while we handle confirmations, communication, and paperwork." },
      ],
    },
    quote: {
      eyebrow: "No Obligation · 24-Hour Response",
      headingStart: "GET YOUR FREE",
      headingEnd: "DISPATCH QUOTE",
      sub: "Fill out the form below and a Superstate dispatcher will reach out within 24 hours with pricing and next steps.",
      labels: {
        fullName: "Full Name *",
        companyName: "Company Name",
        phone: "Phone Number *",
        email: "Email Address *",
        equipment: "Type of Equipment *",
        trucks: "Number of Trucks *",
        regions: "Preferred Operating Regions",
        hasDispatcher: "Do you currently have a dispatcher?",
        message: "Message / Dispatch Needs",
      },
      placeholders: {
        fullName: "John Smith",
        companyName: "Optional",
        phone: "(555) 123-4567",
        email: "you@company.com",
        select: "Select…",
        message: "Tell us about your operation, preferred lanes, target rates, or any specific needs…",
      },
      equipment: ["Dry Van", "Reefer", "Flatbed", "Step Deck", "Box Truck", "Hotshot", "Other"],
      truckCounts: ["1", "2–5", "6–10", "11–20", "20+"],
      regions: ["Southeast", "Northeast", "Midwest", "Southwest", "West Coast", "Nationwide"],
      yes: "Yes",
      no: "No",
      submit: "Request My Free Quote",
      submitting: "Submitting…",
      successHeading: "Request Received!",
      successDesc: "Thank you! A Superstate dispatcher will contact you within 24 hours with your free quote and next steps.",
      submitAnother: "Submit Another Request",
      errors: {
        required: "Required",
        invalidEmail: "Invalid email",
        fixErrors: "Please fix the errors above.",
        genericFail: "Something went wrong. Please call 786-543-8906.",
        success: "Quote request received!",
      },
    },
    testimonials: {
      eyebrow: "Testimonials",
      headingLine1: "TRUSTED BY DRIVERS",
      headingLine2: "ACROSS THE COUNTRY.",
      items: [
        { quote: "Superstate LLC changed the game for me. My truck hasn't sat idle in 3 months and my rates are up 20%. Best dispatcher I've ever worked with.", name: "Marcus T.", role: "Dry Van Owner-Operator", state: "Georgia", initials: "MT" },
        { quote: "I was skeptical at first, but they found me consistent freight on my preferred lanes within the first week. Communication is always on point.", name: "Sandra R.", role: "Reefer Carrier", state: "Texas", initials: "SR" },
        { quote: "Running a 5-truck fleet solo was killing me. Superstate handles all our dispatch and I finally have time to grow the business.", name: "David K.", role: "Small Fleet Owner", state: "Ohio", initials: "DK" },
      ],
    },
    faq: {
      eyebrow: "FAQ",
      headingLine1: "COMMON QUESTIONS,",
      headingLine2: "STRAIGHT ANSWERS.",
      items: [
        { q: "How much does Superstate LLC charge?", a: "We charge a simple flat percentage per load with no hidden fees. Contact us for exact pricing based on your fleet size and equipment type." },
        { q: "What types of trucks do you dispatch?", a: "Dry Van, Reefer, Flatbed, Step Deck, Box Truck, Hotshot, and more. If it rolls, we'll find freight for it." },
        { q: "Do I need to sign a long-term contract?", a: "No lock-in contracts. We earn your business every week with the quality of loads we bring you and the service we deliver." },
        { q: "How fast can I get started?", a: "Most carriers are fully onboarded and dispatched within 24–48 hours of signing up." },
        { q: "Do you work with new authority carriers?", a: "Absolutely. We specialize in helping new owner-operators get their business off the ground and into profitable lanes fast." },
        { q: "What lanes and regions do you cover?", a: "All 48 contiguous states. We focus on your preferred regions and lanes to maximize rate per mile." },
      ],
    },
    finalCta: {
      headingLine1: "READY TO STOP SITTING",
      headingLine2Start: "AND",
      headingLine2End: "START EARNING?",
      sub: "Let Superstate LLC handle the dispatching while you handle the miles. Join hundreds of drivers who trust us to keep them loaded.",
      btnPrimary: "Get Started Today",
      btnSecondary: "Or Call 786-543-8906",
    },
    footer: {
      tagline: "Powering America's Roads, One Load at a Time.",
      quickLinks: "Quick Links",
      contactTitle: "Contact",
      location: "Nationwide · 49 States",
      copyright: "© 2025 Superstate LLC. All Rights Reserved.",
      tagline2Start: "Built for owner-operators.",
      tagline2End: "Trusted nationwide.",
    },
    chat: {
      header: "SUPERSTATE DISPATCH",
      subheader: "Live · Typically replies instantly",
      greeting: "Hey there! I'm the Superstate LLC virtual dispatcher. Ask me anything about our dispatch services — rates, onboarding, equipment, lanes. When you're ready, I can also connect you with a real dispatcher.",
      inputPlaceholder: "Type your message…",
      callPrompt: "Prefer to call? 786-543-8906",
      quickReplies: ["Our Services", "Get a Quote", "Pricing Info", "Call Now"],
      humanFormTitle: "Connect with a dispatcher",
      humanFormName: "Your name",
      humanFormPhone: "Phone number",
      humanFormEmail: "Email",
      humanFormSubmit: "Notify Dispatcher",
      submittedTitle: "Dispatcher notified!",
      submittedDesc: "We'll reach out shortly. Want to talk right now?",
      whatsapp: "WhatsApp",
      call: "Call",
      prefilledMsg: "Hi, I just submitted a dispatch inquiry on superstatedispatch.com — my name is",
      errorToast: "Chat error — please try again.",
      errorAll: "Please fill all fields.",
      errorGeneric: "Something went wrong.",
      successToast: "A dispatcher will reach out shortly!",
    },
    lang: {
      en: "EN",
      es: "ES",
      aria: "Switch language",
    },
  },

  es: {
    nav: {
      home: "Inicio",
      services: "Servicios",
      howItWorks: "Cómo Funciona",
      whyUs: "Por Qué Nosotros",
      blog: "Blog",
      faq: "Preguntas Frecuentes",
      testimonials: "Testimonios",
      quote: "Obtener Cotización",
      callBtn: "Llamar 786-543-8906",
    },
    hero: {
      badge: "Despacho Nacional · 49 Estados",
      headlineWords: ["MANTÉN", "TUS", "RUEDAS", "EN MOVIMIENTO.", "MANTÉN", "TUS", "INGRESOS", "CRECIENDO."],
      goldWords: ["EN MOVIMIENTO.", "CRECIENDO."],
      sub: "Superstate LLC maneja tu despacho, negociaciones con brokers y planificación de cargas — para que te enfoques en el camino y maximices cada milla.",
      ctaPrimary: "Obtener Cotización Gratis",
      ctaSecondary: "Llama Ahora: 786-543-8906",
      scroll: "Desliza",
      trustBadges: [
        "Respuesta en 24 Horas",
        "Despachador Dedicado",
        "Servicio Personalizado",
        "Para Operadores y Flotas",
      ],
      ariaHeadline: "Mantén tus ruedas en movimiento. Mantén tus ingresos creciendo.",
    },
    stats: [
      { value: 500, suffix: "+", label: "Cargas Despachadas al Mes" },
      { value: 98, suffix: "%", label: "Cargas Entregadas a Tiempo" },
      { value: 24, suffix: "/7", label: "Despachador en Vivo" },
      { value: 0, prefix: "$", label: "Sin Tarifas Ocultas. Nunca." },
    ],
    about: {
      eyebrow: "Acerca de Superstate LLC",
      headingStart: "TU OFICINA.",
      headingEnd: "MANEJADA.",
      p1: "No somos solo otro servicio de despacho — somos un socio de crecimiento para tu negocio de camiones. Nuestro equipo lucha por las mejores tarifas, maneja la comunicación con brokers, administra tu papelería y mantiene tus camiones cargados para que nunca estés parado.",
      p2: "Ya seas un operador independiente solo o manejes una flota de 20 camiones, trabajamos como tus gerentes personales de negocios — para que te enfoques en manejar y crecer.",
      capabilitiesTitle: "Capacidades Principales",
      capabilities: [
        "Despacho de Carga Completa (FTL y LTL)",
        "Negociación de Cargas y Maximización de Tarifas",
        "Comunicación con Brokers y Cargadores",
        "Planificación de Rutas y Optimización de Rutas",
        "Manejo de Confirmación de Tarifas",
        "Soporte de Papelería y Administración",
        "Guía de Cumplimiento",
        "Gestión Dedicada de Cuenta",
      ],
    },
    services: {
      eyebrow: "Qué Hacemos",
      headingLine1: "SOLUCIONES DE DESPACHO",
      headingLine2: "PARA TODO CAMIÓN.",
      sub: "Diez pilares de servicio, una misión implacable: mantener tus camiones cargados y tus ingresos subiendo.",
      items: [
        { title: "Despacho de Carga Completa", desc: "Carga completa de camión ajustada a tus rutas y horario — millas consistentes y rentables." },
        { title: "Despacho Reefer", desc: "Cargas con control de temperatura manejadas con precisión y atención a cada detalle." },
        { title: "Despacho Dry Van", desc: "Carga seca de alto volumen en las rutas mejor pagadas con confirmación rápida." },
        { title: "Despacho Flatbed", desc: "Cargas grandes y especializadas negociadas para tarifa máxima y mínimo dolor de cabeza." },
        { title: "Despacho Box Truck", desc: "Cargas locales y regionales diseñadas para flotas pequeñas y operadores independientes de box truck." },
        { title: "Comunicación con Brokers", desc: "Nosotros tratamos con los brokers para que tú nunca tengas que hacerlo — llamadas, correos, rate cons, todo." },
        { title: "Planificación de Rutas", desc: "Rutas inteligentes que reducen las millas vacías y aumentan la ganancia por milla en cada viaje." },
        { title: "Confirmación de Tarifas y Papelería", desc: "Cada documento manejado, cada vez — rate cons, BOLs, facturación y cumplimiento." },
        { title: "Soporte 24/7 de Despachador", desc: "Una persona real al teléfono cuando la necesites — día, noche, fin de semana, festivo." },
        { title: "Incorporación de Carriers", desc: "¿Autoridad nueva? Te ayudamos a configurarte rápido y rodar en 24 a 48 horas." },
      ],
    },
    why: {
      eyebrow: "Por Qué Superstate",
      headingLine1: "NO SOLO DESPACHAMOS.",
      headingLine2: "IMPULSAMOS TU NEGOCIO.",
      quote: [
        "\"La mayoría de despachadores solo buscan cargas. Nosotros buscamos las ",
        "cargas CORRECTAS",
        " — con las ",
        "tarifas CORRECTAS",
        " — en las ",
        "rutas CORRECTAS",
        " para TU negocio.\"",
      ],
      differentiators: [
        { title: "Despachador Dedicado", desc: "Un solo punto de contacto que conoce tu negocio, tus preferencias y tus rutas." },
        { title: "Selección de Cargas Rentables", desc: "Solo reservamos cargas que tengan sentido para tu ganancia — no para llenar la lista." },
        { title: "Cero Tiempo Parado", desc: "Trabajamos adelantados a tu horario para que tu próxima carga esté lista antes de terminar la actual." },
        { title: "Precios Transparentes", desc: "Tarifa fija de despacho. Sin sorpresas. Sin cortes ocultos. Sin letra pequeña." },
        { title: "Comunicación Real", desc: "Contestamos llamadas, respondemos mensajes y estamos pendientes de tu carga — siempre." },
        { title: "Escalable para Flotas", desc: "Ya sea que tengas 1 camión o 20, nuestros sistemas y equipo crecen contigo." },
      ],
    },
    how: {
      eyebrow: "Cómo Funciona",
      headingStart: "PROCESO SIMPLE.",
      headingEnd: "RESULTADOS SERIOS.",
      steps: [
        { title: "Envía Tu Info", desc: "Llena nuestro formulario rápido con tu tipo de equipo, rutas y necesidades de despacho." },
        { title: "Conocemos Tu Negocio", desc: "Tu despachador dedicado aprende tus preferencias, tarifas y metas." },
        { title: "Buscamos y Negociamos", desc: "Buscamos en el mercado, negociamos con brokers y aseguramos carga rentable." },
        { title: "Tú Manejas. Tú Cobras.", desc: "Sal a la carretera mientras nosotros manejamos confirmaciones, comunicación y papelería." },
      ],
    },
    quote: {
      eyebrow: "Sin Compromiso · Respuesta en 24 Horas",
      headingStart: "OBTÉN TU COTIZACIÓN",
      headingEnd: "DE DESPACHO GRATIS",
      sub: "Llena el formulario abajo y un despachador de Superstate se comunicará contigo en 24 horas con precios y próximos pasos.",
      labels: {
        fullName: "Nombre Completo *",
        companyName: "Nombre de Empresa",
        phone: "Número de Teléfono *",
        email: "Correo Electrónico *",
        equipment: "Tipo de Equipo *",
        trucks: "Número de Camiones *",
        regions: "Regiones Preferidas de Operación",
        hasDispatcher: "¿Tienes actualmente un despachador?",
        message: "Mensaje / Necesidades de Despacho",
      },
      placeholders: {
        fullName: "Juan Pérez",
        companyName: "Opcional",
        phone: "(555) 123-4567",
        email: "tu@empresa.com",
        select: "Selecciona…",
        message: "Cuéntanos sobre tu operación, rutas preferidas, tarifas objetivo o cualquier necesidad específica…",
      },
      equipment: ["Dry Van", "Reefer", "Flatbed", "Step Deck", "Box Truck", "Hotshot", "Otro"],
      truckCounts: ["1", "2–5", "6–10", "11–20", "20+"],
      regions: ["Sureste", "Noreste", "Medio Oeste", "Suroeste", "Costa Oeste", "Nacional"],
      yes: "Sí",
      no: "No",
      submit: "Solicitar Mi Cotización Gratis",
      submitting: "Enviando…",
      successHeading: "¡Solicitud Recibida!",
      successDesc: "¡Gracias! Un despachador de Superstate te contactará en 24 horas con tu cotización gratis y los próximos pasos.",
      submitAnother: "Enviar Otra Solicitud",
      errors: {
        required: "Requerido",
        invalidEmail: "Correo inválido",
        fixErrors: "Por favor corrige los errores arriba.",
        genericFail: "Algo salió mal. Por favor llama al 786-543-8906.",
        success: "¡Solicitud de cotización recibida!",
      },
    },
    testimonials: {
      eyebrow: "Testimonios",
      headingLine1: "CON LA CONFIANZA DE CONDUCTORES",
      headingLine2: "EN TODO EL PAÍS.",
      items: [
        { quote: "Superstate LLC cambió el juego para mí. Mi camión no ha estado parado en 3 meses y mis tarifas subieron 20%. El mejor despachador con el que he trabajado.", name: "Marcus T.", role: "Operador Independiente Dry Van", state: "Georgia", initials: "MT" },
        { quote: "Al principio fui escéptico, pero me encontraron carga constante en mis rutas preferidas en la primera semana. La comunicación siempre está al punto.", name: "Sandra R.", role: "Carrier Reefer", state: "Texas", initials: "SR" },
        { quote: "Manejar una flota de 5 camiones solo me estaba matando. Superstate maneja todo nuestro despacho y finalmente tengo tiempo para hacer crecer el negocio.", name: "David K.", role: "Dueño de Flota Pequeña", state: "Ohio", initials: "DK" },
      ],
    },
    faq: {
      eyebrow: "Preguntas Frecuentes",
      headingLine1: "PREGUNTAS COMUNES,",
      headingLine2: "RESPUESTAS DIRECTAS.",
      items: [
        { q: "¿Cuánto cobra Superstate LLC?", a: "Cobramos un porcentaje fijo simple por carga sin tarifas ocultas. Contáctanos para un precio exacto basado en el tamaño de tu flota y tipo de equipo." },
        { q: "¿Qué tipos de camiones despachan?", a: "Dry Van, Reefer, Flatbed, Step Deck, Box Truck, Hotshot y más. Si rueda, le encontramos carga." },
        { q: "¿Necesito firmar un contrato a largo plazo?", a: "Sin contratos de amarre. Nos ganamos tu negocio cada semana con la calidad de las cargas y el servicio que entregamos." },
        { q: "¿Qué tan rápido puedo empezar?", a: "La mayoría de carriers quedan completamente incorporados y despachados en 24 a 48 horas después de registrarse." },
        { q: "¿Trabajan con carriers de autoridad nueva?", a: "Por supuesto. Nos especializamos en ayudar a operadores independientes nuevos a arrancar su negocio y entrar a rutas rentables rápido." },
        { q: "¿Qué rutas y regiones cubren?", a: "Los 48 estados continuos. Nos enfocamos en tus regiones y rutas preferidas para maximizar la tarifa por milla." },
      ],
    },
    finalCta: {
      headingLine1: "¿LISTO PARA DEJAR DE ESPERAR",
      headingLine2Start: "Y",
      headingLine2End: "EMPEZAR A GANAR?",
      sub: "Deja que Superstate LLC maneje el despacho mientras tú manejas las millas. Únete a cientos de conductores que confían en nosotros para mantenerlos cargados.",
      btnPrimary: "Comenzar Hoy",
      btnSecondary: "O Llama 786-543-8906",
    },
    footer: {
      tagline: "Impulsando las Carreteras de América, Una Carga a la Vez.",
      quickLinks: "Enlaces Rápidos",
      contactTitle: "Contacto",
      location: "Nacional · 49 Estados",
      copyright: "© 2025 Superstate LLC. Todos los Derechos Reservados.",
      tagline2Start: "Hecho para operadores independientes.",
      tagline2End: "Confianza nacional.",
    },
    chat: {
      header: "SUPERSTATE DESPACHO",
      subheader: "En vivo · Normalmente responde al instante",
      greeting: "¡Hola! Soy el despachador virtual de Superstate LLC. Pregúntame lo que quieras sobre nuestros servicios — tarifas, incorporación, equipos, rutas. Cuando estés listo, también te puedo conectar con un despachador real.",
      inputPlaceholder: "Escribe tu mensaje…",
      callPrompt: "¿Prefieres llamar? 786-543-8906",
      quickReplies: ["Nuestros Servicios", "Obtener Cotización", "Info de Precios", "Llamar Ahora"],
      humanFormTitle: "Conectar con un despachador",
      humanFormName: "Tu nombre",
      humanFormPhone: "Número de teléfono",
      humanFormEmail: "Correo electrónico",
      humanFormSubmit: "Notificar al Despachador",
      submittedTitle: "¡Despachador notificado!",
      submittedDesc: "Te contactaremos pronto. ¿Quieres hablar ahora mismo?",
      whatsapp: "WhatsApp",
      call: "Llamar",
      prefilledMsg: "Hola, acabo de enviar una consulta de despacho en superstatedispatch.com — mi nombre es",
      errorToast: "Error del chat — intenta de nuevo.",
      errorAll: "Por favor llena todos los campos.",
      errorGeneric: "Algo salió mal.",
      successToast: "¡Un despachador se comunicará contigo pronto!",
    },
    lang: {
      en: "EN",
      es: "ES",
      aria: "Cambiar idioma",
    },
  },
};

export const getT = (lang) => translations[lang] || translations.en;
